/* eslint-disable sonarjs/cognitive-complexity -- Helper function */

/* eslint-disable max-lines -- Helper function */
import { v4 as uuidv4 } from "uuid";
import {
  getHeightOptions,
  getSizingOptions,
  getWidthOptions,
} from "@/react/components/shared/BuilderUI/utils/sizeStyleHelpers";
import {
  getColorOptions,
  getFontSizeOptions,
  getFontWeightOptions,
  getTextAlignOptions,
} from "@/react/components/shared/BuilderUI/utils/textStyleHelpers";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { defaultData as CardDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/CallToAction/CardCallToAction/CardCallToActionDefaultData";
import { defaultData as HrDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Content/LineBlock/HorizontalLineDefaultData";
import { defaultData as FeatureCard2DefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/FeatureCards/FeatureCard2/FeatureCard2DefaultData";
import { defaultData as ContactItemDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Sections/ContactItemBlock/ContactItemBlockDefaultData";
import { defaultData as FaqItemDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Sections/FaqItemBlock/FaqItemDefaultData";
import { defaultData as ItemCardDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Sections/ItemCardBlock/ItemCardDefaultData";
import { defaultData as NumberedCardDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Sections/NumberedCardBlock/NumberedCardBlockDefaultData";
import { defaultData as StatCardDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Sections/StatCardBlock/StatCardBlockDefaultData";
import { defaultData as UserCardDefaultData } from "@circle-react-shared/BuilderUI/DefaultBlocks/Sections/UserCardBlock/UserCardDefaultData";
import { createImageBlock } from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/mediaBlockHelpers";
import {
  createButtonBlock,
  createH3Block,
  createH4Block,
  createH5Block,
  createPBlock,
  createUlBlock,
} from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/textBlocksHelpers";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { getBackgroundColorOptions } from "@circle-react-shared/BuilderUI/utils/buttonStyleHelpers";
import { getMarginOptions } from "../../utils/boxModelStyleHelpers";
import { getBorderRadiusOptions } from "../../utils/imgStyleHelpers";

export const createHrBlock = ({
  baseData = HrDefaultData,
  styles = {},
}: {
  baseData?: BaseBlock;
  styles?: Record<string, StyleValue>;
} = {}): BaseBlock => ({
  ...baseData,
  id: uuidv4(),
  props: {
    ...baseData.props,
    styles: {
      ...baseData?.props?.styles,
      prop: "styles",
      type: "styles",
      value: {
        ...(baseData?.props?.styles?.value as Record<string, StyleValue>),
        ...styles,
      },
    },
  },
});

export const createCardBlock = ({
  highlight = "",
  image = { signedId: "", placeholder: "" },
  h5Text = "",
  pText = "",
  ulList = [],
  hasDivider = false,
  description = "",
  button = {
    text: "",
    href: "",
  },
  value = [],
  styles = {},
}: {
  highlight?: string;
  image?: {
    signedId: string;
    placeholder?: string;
  };
  h5Text?: string;
  pText?: string;
  ulList?: string[] | { text: string; placeholder?: string }[];
  hasDivider?: boolean;
  description?: string;
  button?: {
    text: string;
    href: string;
  };
  value?: BaseBlock[];
  styles?: Record<string, StyleValue>;
}): BaseBlock => ({
  ...CardDefaultData,
  id: uuidv4(),
  props: {
    ...CardDefaultData.props,
    highlight: {
      ...CardDefaultData.props.highlight,
      prop: "other",
      type: "string",
      value: highlight,
    },
    content: {
      ...CardDefaultData.props.content,
      type: "children",
      prop: "children",
      value:
        value && value.length > 0
          ? value
          : [
              ...(image && "signedId" in image
                ? [
                    createImageBlock({
                      signedId: image.signedId,
                      placeholder: image.placeholder,
                      styles: {
                        width: getWidthOptions({
                          defaultValue: "3.375rem",
                        }),
                        height: getHeightOptions({
                          defaultValue: "3.375rem",
                        }),
                        borderRadius: getBorderRadiusOptions({
                          defaultValue: {
                            border_left_top_radius: 100,
                            border_left_bottom_radius: 100,
                            border_right_top_radius: 100,
                            border_right_bottom_radius: 100,
                          },
                        }),
                        backgroundColor: getBackgroundColorOptions({
                          defaultValue: "var(--theme-website-color-5)",
                        }),
                      },
                    }),
                  ]
                : []),
              ...(h5Text
                ? [
                    createH5Block({
                      text: h5Text,
                    }),
                  ]
                : []),
              ...(pText
                ? [
                    createPBlock({
                      text: pText,
                      paragraphStyle: "p3",
                    }),
                  ]
                : []),
              ...(ulList && ulList.length > 0
                ? [
                    createUlBlock({
                      items: ulList,
                    }),
                  ]
                : []),

              ...(hasDivider ? [createHrBlock()] : []),

              ...(description
                ? [
                    createPBlock({
                      text: description,
                      styles: {
                        color: getColorOptions(
                          "var(--theme-website-color-text-secondary)",
                        ),
                        fontWeight: getFontWeightOptions("500"),
                        fontSize: getFontSizeOptions("1rem"),
                      },
                    }),
                  ]
                : []),
              ...(button && button.text && button.href
                ? [
                    createButtonBlock({
                      text: button.text,
                      href: button.href,
                      styles: {
                        margin: getMarginOptions({
                          defaultValue: {
                            marginTop: "20",
                            marginBottom: "0",
                            marginLeft: "0",
                            marginRight: "0",
                          },
                        }),
                      },
                    }),
                  ]
                : []),
            ].filter(Boolean),
    },
    styles: {
      ...CardDefaultData.props?.styles,
      prop: "styles",
      type: "styles",
      value: {
        ...(CardDefaultData.props?.styles?.value as Record<string, StyleValue>),
        ...styles,
      },
    },
  },
});

export const createNumberedCardBlock = ({
  number,
  title,
  description,
}: {
  number: string;
  title: string;
  description: string;
}): BaseBlock => ({
  ...NumberedCardDefaultData,
  id: uuidv4(),
  props: {
    ...NumberedCardDefaultData.props,
    number: {
      ...NumberedCardDefaultData.props.number,
      type: "children",
      prop: "children",
      value: [
        createH3Block({
          text: number,
          styles: {
            textAlign: getTextAlignOptions("center"),
            fontWeight: getFontWeightOptions("semibold"),
            color: getColorOptions(
              "var(--theme-website-primary-button-default-text-color)",
            ),
            fontSize: getFontSizeOptions("1.25rem"),
            width: getWidthOptions({
              defaultValue: "100%",
            }),
          },
        }),
      ],
    },
    content: {
      ...NumberedCardDefaultData.props.content,
      type: "children",
      prop: "children",
      value: [
        createH5Block({
          text: title,
        }),
        createPBlock({
          text: description,
          paragraphStyle: "p3",
        }),
      ],
    },
  },
});

export const createFeatureCard2Block = ({
  icon = { signedId: "", placeholder: "" },
  titleContent = "",
  image = { signedId: "", placeholder: "" },
  value = [],
  description = "",
}: {
  icon?: {
    signedId: string;
    placeholder?: string;
  };
  titleContent?: string;
  image?: {
    signedId: string;
    placeholder?: string;
  };
  description?: string;
  value?: BaseBlock[];
}): BaseBlock => ({
  ...FeatureCard2DefaultData,
  id: uuidv4(),
  props: {
    ...FeatureCard2DefaultData.props,
    icon: {
      prop: "children",
      type: "children",
      value:
        value?.length > 0
          ? value
          : [
              createImageBlock({
                signedId: icon.signedId,
                placeholder: icon.placeholder,
                styles: {
                  borderRadius: getBorderRadiusOptions({
                    defaultValue: {
                      border_left_top_radius: 100,
                      border_left_bottom_radius: 100,
                      border_right_top_radius: 100,
                      border_right_bottom_radius: 100,
                    },
                  }),
                  sizing: getSizingOptions({
                    defaultValue: {
                      width: "54px",
                      height: "54px",
                      minWidth: "54px",
                      minHeight: "54px",
                      maxWidth: "54px",
                      maxHeight: "54px",
                    },
                  }),
                },
              }),
            ],
    },
    titleContent: {
      prop: "children",
      type: "children",
      value: [createH4Block({ text: titleContent })],
    },
    image: {
      prop: "children",
      type: "children",
      value: [
        createImageBlock({
          signedId: image.signedId,
          placeholder: image.placeholder,
          styles: {
            sizing: getSizingOptions({
              defaultValue: {
                width: "100%",
                height: "54px",
                minWidth: "100%",
                minHeight: "54px",
                maxWidth: "100%",
                maxHeight: "54px",
              },
            }),
          },
        }),
      ],
    },
    description: {
      prop: "children",
      type: "children",
      value: [createPBlock({ text: description })],
    },
  },
});

export const createUserCardBlock = ({
  image = { signedId: "", placeholder: "" },
  header = "",
  description = "",
  size = "normal",
}: {
  image?: { signedId: string; placeholder?: string };
  header?: string;
  description?: string;
  size?: "normal" | "small";
}): BaseBlock => ({
  ...UserCardDefaultData,
  id: uuidv4(),
  props: {
    ...UserCardDefaultData.props,
    image: {
      ...UserCardDefaultData.props.image,
      prop: "children",
      type: "children",
      value: [
        createImageBlock({
          signedId: image.signedId,
          placeholder: image.placeholder,
          styles: {
            sizing: getSizingOptions({
              defaultValue: {
                width: "100%",
                height: "auto",
                minWidth: size === "normal" ? "48px" : "40px",
                minHeight: size === "normal" ? "48px" : "40px",
                maxWidth: size === "normal" ? "48px" : "40px",
                maxHeight: size === "normal" ? "48px" : "40px",
              },
            }),
            borderRadius: getBorderRadiusOptions({
              defaultValue: {
                border_left_top_radius: 20,
                border_left_bottom_radius: 20,
                border_right_top_radius: 20,
                border_right_bottom_radius: 20,
              },
            }),
          },
        }),
      ],
    },
    header: {
      ...UserCardDefaultData.props.header,
      prop: "children",
      type: "children",
      value: [
        createH5Block({
          text: header,
          styles: {
            fontSize: getFontSizeOptions(
              size === "normal" ? "1.125rem" : "0.875rem",
            ),
            fontWeight: getFontWeightOptions("500"),
          },
        }),
      ],
    },
    description: {
      ...UserCardDefaultData.props.description,
      prop: "children",
      type: "children",
      value: [
        createPBlock({
          text: description,
          paragraphStyle: "p3",
          styles: {
            fontSize: getFontSizeOptions(
              size === "normal" ? "1rem" : "0.875rem",
            ),
          },
        }),
      ],
    },
  },
});

export const createItemCardBlock = ({
  image = { signedId: "", placeholder: "" },
  title = "",
  description = "",
}): BaseBlock => ({
  ...ItemCardDefaultData,
  id: uuidv4(),
  props: {
    ...ItemCardDefaultData.props,
    image: {
      ...ItemCardDefaultData.props.image,
      prop: "children",
      type: "children",
      value: [
        createImageBlock({
          signedId: image.signedId,
          placeholder: image.placeholder,
          styles: {
            sizing: getSizingOptions({
              defaultValue: {
                width: "48px",
                height: "48px",
                minWidth: "none",
                minHeight: "none",
                maxWidth: "none",
                maxHeight: "none",
              },
            }),
          },
        }),
      ],
    },
    header: {
      ...ItemCardDefaultData.props.header,
      prop: "children",
      type: "children",
      value: [
        createH5Block({
          text: title,
        }),
      ],
    },
    description: {
      ...ItemCardDefaultData.props.description,
      prop: "children",
      type: "children",
      value: [
        createPBlock({
          text: description,
          styles: {
            color: getColorOptions("var(--theme-website-color-text-muted)"),
          },
        }),
      ],
    },
  },
});

export const createFaqItemBlock = ({
  question = "",
  answer = "",
}: {
  question?: string;
  answer?: string;
}): BaseBlock => ({
  ...FaqItemDefaultData,
  id: uuidv4(),
  props: {
    ...FaqItemDefaultData.props,
    header: {
      ...FaqItemDefaultData.props.header,
      prop: "children",
      type: "children",
      value: [
        createH5Block({
          text: question,
        }),
      ],
    },
    content: {
      ...FaqItemDefaultData.props.content,
      prop: "children",
      type: "children",
      value: [
        createPBlock({
          text: answer,
        }),
      ],
    },
  },
});

export const createContactItemBlock = ({
  image = { signedId: "", placeholder: "" },
  text,
}: {
  image: { signedId: string; placeholder?: string };
  text: string;
}): BaseBlock => ({
  ...ContactItemDefaultData,
  id: uuidv4(),
  props: {
    ...ContactItemDefaultData.props,
    content: {
      ...ContactItemDefaultData.props.content,
      prop: "children",
      type: "children",
      value: [
        createImageBlock({
          signedId: image.signedId,
          placeholder: image.placeholder,
          styles: {
            sizing: getSizingOptions({
              defaultValue: {
                width: "auto",
                height: "2.375rem",
                minWidth: "none",
                minHeight: "none",
                maxWidth: "none",
                maxHeight: "none",
              },
            }),
          },
        }),
        createPBlock({
          text,
        }),
      ],
    },
  },
});

export const createStatCardBlock = ({
  number,
  text,
  baseData = StatCardDefaultData,
}: {
  number: string;
  text: string;
  baseData?: BaseBlock;
}): BaseBlock => ({
  ...baseData,
  id: uuidv4(),
  props: {
    ...baseData.props,
    content: {
      ...baseData.props.content,
      type: "children",
      prop: "children",
      value: [
        createH3Block({
          text: number,
          styles: {
            color: getColorOptions("var(--theme-website-color-text-secondary)"),
          },
        }),
        createPBlock({
          text,
        }),
      ],
    },
  },
});
